/**
 * Tab Data Prefetcher - Warms React Query cache for ALL tabs on hover/touch and after auth ready
 * 
 * Strategy:
 * 1. Prefetch route chunks for all tabs after bootstrap (DashboardTabContent handles this)
 * 2. Prefetch data for all tabs after auth ready
 * 3. Prefetch on hover/touch for instant tab switching
 */

import { useCallback, useEffect, useRef } from "react";
import { useQueryClient, QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfDay } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { preloadRoute } from "@/hooks/useRoutePreloader";

// ==================== Query Keys ====================
// Centralized keys for cache coordination across the app

export const tabQueryKeys = {
  // Home Tab
  homeTab: {
    healthData: (userId: string) => ["homeTab", "healthData", "v2", userId] as const,
  },
  // Meds Tab
  medsTab: {
    prescriptions: (userId: string) => ["medsTab", "prescriptions", userId] as const,
    takenMeds: (userId: string) => ["medsTab", "takenMeds", userId] as const,
  },
  // Appointments Tab
  appointmentsTab: {
    upcomingAppointments: ["upcoming-appointments"] as const,
    careTeamMembers: ["care-team-members"] as const,
    // Main appointments list used by AppointmentManager (useAppointmentsQuery)
    appointmentsList: (userId: string) => ["appointments", "list", userId] as const,
  },
  // Track Tab
  trackTab: {
    cyclePreference: (userId: string) => ["cycleTrackingPreference", userId] as const,
    recentLogs: (userId: string) => ["trackTab", "recentLogs", userId] as const,
  },
  // More Tab
  moreTab: {
    subscription: (userId: string) => ["subscription", userId] as const,
    adminStatus: (userId: string) => ["adminStatus", userId] as const,
  },
  // Beta Tab
  betaTab: {
    betaStatus: (userId: string) => ["betaTab", "status", userId] as const,
    betaDraft: (userId: string) => ["betaTab", "draft", userId] as const,
  },
  // Standalone pages (prefetched in idle time)
  careTeamPage: {
    members: ["care-team-members"] as const,
    appointmentsAutocomplete: ["appointments-autocomplete"] as const,
  },
  historyPage: {
    day: (userId: string, dateISO: string) => ["history-day", userId, dateISO] as const,
    calendarDates: (userId: string) => ["history-calendar-dates", userId] as const,
  },
};

// Track prefetched tabs to avoid duplicate fetches per session
const prefetchedTabs = new Set<string>();

// ==================== Data Fetchers ====================

async function fetchHomeTabData(userId: string) {
  const sevenDaysAgo = format(subDays(new Date(), 7), "yyyy-MM-dd");
  const thirtyDaysAgo = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const today = format(new Date(), "yyyy-MM-dd");
  const weekFromNow = format(subDays(new Date(), -7), "yyyy-MM-dd");

  const [
    { data: symptoms },
    { data: weekSymptoms },
    { data: recentAllSymptoms },
    { data: appointments },
  ] = await Promise.all([
    supabase
      .from("symptoms")
      .select("logged_date")
      .eq("user_id", userId)
      .order("logged_date", { ascending: false })
      .limit(30),
    supabase
      .from("symptoms")
      .select("logged_date, intensity")
      .eq("user_id", userId)
      .gte("logged_date", sevenDaysAgo),
    supabase
      .from("symptoms")
      .select("symptom_name, intensity")
      .eq("user_id", userId)
      .gte("logged_date", thirtyDaysAgo),
    supabase
      .from("appointments")
      .select("id, provider_name, specialty, appointment_date")
      .eq("user_id", userId)
      .eq("completed", false)
      .gte("appointment_date", today)
      .lte("appointment_date", weekFromNow)
      .order("appointment_date", { ascending: true }),
  ]);

  // Calculate streak
  let trackingStreak = 0;
  if (symptoms && symptoms.length > 0) {
    const todayDate = startOfDay(new Date());
    for (let i = 0; i < 30; i++) {
      const checkDate = format(subDays(todayDate, i), "yyyy-MM-dd");
      const hasEntry = symptoms.some((s) => s.logged_date === checkDate);
      if (hasEntry) trackingStreak++;
      else if (i > 0) break;
    }
  }

  // Weekly data
  const weekData = [];
  for (let i = 6; i >= 0; i--) {
    const date = format(subDays(new Date(), i), "yyyy-MM-dd");
    const dayName = format(subDays(new Date(), i), "EEE");
    const daySymptoms = weekSymptoms?.filter((s) => s.logged_date === date) || [];
    const avgIntensity =
      daySymptoms.length > 0
        ? daySymptoms.reduce((sum, s) => sum + (s.intensity || 0), 0) / daySymptoms.length
        : 0;
    weekData.push({ day: dayName, severity: Math.round(avgIntensity * 10) / 10, count: daySymptoms.length });
  }

  // Average only across days that actually have entries — empty days must not
  // count as "no pain" (that inflated energy for sparse loggers).
  const loggedDays = weekData.filter((d) => d.count > 0);
  const avgWeeklySeverity =
    loggedDays.length > 0
      ? loggedDays.reduce((sum, d) => sum + d.severity, 0) / loggedDays.length
      : 0;
  // Intensity is a 1-10 scale, so 10 points of energy per point of severity.
  const energyLevel =
    loggedDays.length === 0
      ? 100
      : Math.round(Math.max(0, Math.min(100, 100 - avgWeeklySeverity * 10)));

  let averagePainLevel = 0;
  if (weekSymptoms && weekSymptoms.length > 0) {
    averagePainLevel =
      Math.round(
        (weekSymptoms.reduce((sum, s) => sum + (s.intensity || 0), 0) / weekSymptoms.length) * 10
      ) / 10;
  }

  // Top symptoms
  const topSymptoms: { name: string; count: number; avgIntensity: number }[] = [];
  if (recentAllSymptoms && recentAllSymptoms.length > 0) {
    const symptomMap = new Map<string, { count: number; totalIntensity: number }>();
    recentAllSymptoms.forEach((symptom) => {
      const existing = symptomMap.get(symptom.symptom_name || "") || { count: 0, totalIntensity: 0 };
      symptomMap.set(symptom.symptom_name || "", {
        count: existing.count + 1,
        totalIntensity: existing.totalIntensity + (symptom.intensity || 0),
      });
    });
    const symptomArray = Array.from(symptomMap.entries()).map(([name, data]) => ({
      name,
      count: data.count,
      avgIntensity: data.totalIntensity / data.count,
    }));
    topSymptoms.push(...symptomArray.sort((a, b) => b.count - a.count).slice(0, 5));
  }

  return {
    trackingStreak,
    weeklySymptoms: weekData,
    energyLevel,
    averagePainLevel,
    topSymptoms,
    nextAppointment: appointments?.[0] || null,
    upcomingAppointmentsCount: appointments?.length || 0,
    allUpcomingAppointments: appointments || [],
    hasAnyData: (symptoms && symptoms.length > 0) || (appointments && appointments.length > 0),
  };
}

async function fetchMedsPrescriptions(userId: string) {
  const { data } = await supabase
    .from("prescriptions")
    .select("*")
    .eq("user_id", userId)
    .eq("is_active", true)
    .order("created_at", { ascending: false });
  return data || [];
}

async function fetchMedsTakenToday(userId: string) {
  const today = format(new Date(), "yyyy-MM-dd");
  const { data } = await supabase
    .from("medications")
    .select("medication_name")
    .eq("user_id", userId)
    .eq("logged_date", today)
    .eq("taken", true);
  return data?.map((m) => m.medication_name) || [];
}

async function fetchUpcomingAppointments(userId: string) {
  const now = new Date();
  // Extended to 60 days for better prefetch coverage
  const sixtyDays = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
  const { data } = await supabase
    .from("appointments")
    .select("*")
    .eq("user_id", userId)
    .eq("completed", false)
    .gte("appointment_date", now.toISOString())
    .lte("appointment_date", sixtyDays.toISOString())
    .order("appointment_date", { ascending: true })
    .limit(25);
  return data || [];
}

/**
 * Fetch ALL appointments for AppointmentManager (matches useAppointmentsQuery.ts exactly)
 */
async function fetchAllAppointments(userId: string) {
  const { data } = await supabase
    .from("appointments")
    .select("*")
    .eq("user_id", userId)
    .order("appointment_date", { ascending: true });
  return data || [];
}

async function fetchCareTeamMembers(userId: string) {
  const { data } = await supabase
    .from("care_team_members")
    .select("*")
    .eq("user_id", userId)
    .order("provider_name", { ascending: true });
  return data || [];
}


async function fetchCyclePreference(userId: string) {
  const { data } = await supabase
    .from("user_settings")
    .select("show_cycle_tracking")
    .eq("user_id", userId)
    .maybeSingle();
  return data?.show_cycle_tracking ?? true;
}

async function fetchBetaStatus(userId: string) {
  // Check admin status
  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  
  // Check beta tester status
  const { data: betaTester } = await supabase
    .from("beta_testers")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  const isAdminOrBeta = !!roles || !!betaTester;
  
  if (!isAdminOrBeta) {
    return { isEnabled: false, hasDraft: false };
  }

  // Check settings
  const { data: settings } = await supabase
    .from("beta_settings")
    .select("feedback_enabled")
    .eq("user_id", userId)
    .maybeSingle();

  // Check for draft
  const { data: draft } = await supabase
    .from("beta_feedback")
    .select("id, updated_at")
    .eq("user_id", userId)
    .eq("status", "draft")
    .order("updated_at", { ascending: false })
    .limit(1);

  return {
    isEnabled: settings?.feedback_enabled !== false,
    hasDraft: (draft?.length ?? 0) > 0,
    draftUpdatedAt: draft?.[0]?.updated_at || null,
  };
}

async function fetchSubscriptionStatus(userId: string) {
  const { data } = await supabase
    .from("user_subscriptions")
    .select("tier, expires_at")
    .eq("user_id", userId)
    .maybeSingle();
  
  const { data: betaTester } = await supabase
    .from("beta_testers")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  return {
    tier: data?.tier || "core",
    expiresAt: data?.expires_at,
    isBetaTester: !!betaTester,
    isUpgraded: data?.tier === "premium" || data?.tier === "lifetime" || data?.tier === "upgraded",
  };
}

async function fetchAdminStatus(userId: string) {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  return !!data;
}

/**
 * Fetch history for today via edge function (for prefetch)
 */
async function fetchHistoryToday(userId: string) {
  const today = format(new Date(), "yyyy-MM-dd");
  const { data, error } = await supabase.functions.invoke("get-history-day", {
    body: { date: today, includeCalendarDates: true },
  });
  if (error) throw error;
  return data;
}

/**
 * Fetch appointments for autocomplete (Care Team page needs this)
 */
async function fetchAppointmentsAutocomplete(userId: string) {
  const { data } = await supabase
    .from("appointments")
    .select("provider_name, specialty")
    .order("appointment_date", { ascending: false });
  return data || [];
}

// ==================== Prefetch Functions ====================

type TabKey = "home" | "track" | "meds" | "appointments" | "more" | "beta";
type PageKey = "care-team" | "history";

/**
 * Prefetch data for a specific tab
 */
async function prefetchTabDataInternal(
  queryClient: QueryClient,
  tab: TabKey,
  userId: string
) {
  const cacheKey = `data-${tab}-${userId}`;
  if (prefetchedTabs.has(cacheKey)) return;
  prefetchedTabs.add(cacheKey);

  try {
    switch (tab) {
      case "home":
        await queryClient.prefetchQuery({
          queryKey: tabQueryKeys.homeTab.healthData(userId),
          queryFn: () => fetchHomeTabData(userId),
          staleTime: 1000 * 60 * 2,
        });
        break;

      case "meds":
        await Promise.all([
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.medsTab.prescriptions(userId),
            queryFn: () => fetchMedsPrescriptions(userId),
            staleTime: 1000 * 60 * 5,
          }),
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.medsTab.takenMeds(userId),
            queryFn: () => fetchMedsTakenToday(userId),
            staleTime: 1000 * 60 * 1,
          }),
        ]);
        break;

      case "appointments":
        perfLog('appointments_prefetch_start', { userId });
        await Promise.all([
          // CRITICAL: Prefetch the full appointments list used by AppointmentManager
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.appointmentsTab.appointmentsList(userId),
            queryFn: () => fetchAllAppointments(userId),
            staleTime: 1000 * 60 * 2,
          }),
          // Also prefetch upcoming appointments widget data
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.appointmentsTab.upcomingAppointments,
            queryFn: () => fetchUpcomingAppointments(userId),
            staleTime: 1000 * 60 * 2,
          }),
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.appointmentsTab.careTeamMembers,
            queryFn: () => fetchCareTeamMembers(userId),
            staleTime: 1000 * 60 * 5,
          }),
        ]);
        perfLog('appointments_prefetch_done', { userId });
        break;

      case "track":
        await queryClient.prefetchQuery({
          queryKey: tabQueryKeys.trackTab.cyclePreference(userId),
          queryFn: () => fetchCyclePreference(userId),
          staleTime: 1000 * 60 * 30,
        });
        break;

      case "more":
        await Promise.all([
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.moreTab.subscription(userId),
            queryFn: () => fetchSubscriptionStatus(userId),
            staleTime: 1000 * 60 * 5,
          }),
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.moreTab.adminStatus(userId),
            queryFn: () => fetchAdminStatus(userId),
            staleTime: 1000 * 60 * 10,
          }),
        ]);
        break;

      case "beta":
        await queryClient.prefetchQuery({
          queryKey: tabQueryKeys.betaTab.betaStatus(userId),
          queryFn: () => fetchBetaStatus(userId),
          staleTime: 1000 * 60 * 5,
        });
        break;
    }
  } catch {
    // Silent fail - prefetch is best-effort
    prefetchedTabs.delete(cacheKey);
  }
}

// DEV-only perf instrumentation
const isDev = import.meta.env.DEV;
const perfLog = (msg: string, extra?: Record<string, unknown>) => {
  if (isDev) {
    console.log(`[Prefetch] ${msg}`, performance.now().toFixed(1) + 'ms', extra ? JSON.stringify(extra) : '');
  }
};

/**
 * Prefetch data for standalone pages (Care Team, History)
 * Called during idle time after first paint
 */
async function prefetchPageDataInternal(
  queryClient: QueryClient,
  page: PageKey,
  userId: string
) {
  const cacheKey = `page-${page}-${userId}`;
  if (prefetchedTabs.has(cacheKey)) {
    perfLog(`${page} already prefetched, skipping`);
    return;
  }
  prefetchedTabs.add(cacheKey);

  perfLog(`${page} prefetch_start`);
  const startTime = performance.now();

  try {
    switch (page) {
      case "care-team":
        // Preload the route chunk (uses "careTeam" key in preloader)
        preloadRoute("careTeam");
        
        // Prefetch care team members + appointments autocomplete
        await Promise.all([
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.careTeamPage.members,
            queryFn: () => fetchCareTeamMembers(userId),
            staleTime: 1000 * 60 * 5,
          }),
          queryClient.prefetchQuery({
            queryKey: tabQueryKeys.careTeamPage.appointmentsAutocomplete,
            queryFn: () => fetchAppointmentsAutocomplete(userId),
            staleTime: 1000 * 60 * 10,
          }),
        ]);
        perfLog(`care-team prefetch_done`, { duration_ms: Math.round(performance.now() - startTime) });
        break;

      case "history":
        // Preload the route chunk
        preloadRoute("history");
        
        // Prefetch today's history data
        const today = format(new Date(), "yyyy-MM-dd");
        await queryClient.prefetchQuery({
          queryKey: tabQueryKeys.historyPage.day(userId, today),
          queryFn: () => fetchHistoryToday(userId),
          staleTime: 1000 * 60 * 5,
        });
        perfLog(`history prefetch_done`, { duration_ms: Math.round(performance.now() - startTime) });
        break;
    }
  } catch (err) {
    // Log failures in dev
    if (isDev) {
      console.warn(`[Prefetch] ${page} failed:`, err);
    }
    prefetchedTabs.delete(cacheKey);
  }
}

/**
 * Hook for on-demand tab data prefetching (hover/touch)
 */
export function useTabDataPrefetcher() {
  const queryClient = useQueryClient();

  const prefetchTabData = useCallback(
    async (tab: TabKey) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await prefetchTabDataInternal(queryClient, tab, user.id);
    },
    [queryClient]
  );

  return { prefetchTabData };
}

/**
 * Hook that prefetches ALL tab data after auth is ready
 * Call this once in Dashboard component after auth resolves
 * 
 * OPTIMIZED: Uses staggered scheduling to avoid blocking first paint
 */
export function useGlobalTabPrefetch() {
  const queryClient = useQueryClient();
  const { user, isAuthReady } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    if (!isAuthReady || !user?.id || hasRun.current) return;
    hasRun.current = true;

    const userId = user.id;
    perfLog('global_prefetch_start (auth_ready)', { userId: userId.slice(0, 8) });

    // CRITICAL: Wait for first paint before prefetching to avoid competing with render
    const waitForFirstPaint = (callback: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          perfLog('first_paint_complete');
          callback();
        });
      });
    };

    // Schedule prefetch in idle time, prioritized by importance
    const scheduleIdle = (callback: () => void, priority: "high" | "low" = "low") => {
      if ("requestIdleCallback" in window) {
        const timeout = priority === "high" ? 2000 : 5000;
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
          .requestIdleCallback(callback, { timeout });
      } else {
        const delay = priority === "high" ? 100 : 500;
        setTimeout(callback, delay);
      }
    };

    // Wait for first paint, then start prefetching
    waitForFirstPaint(() => {
      // Priority 1: Home data - MUST prefetch since user lands here first
      // Previous comment said "let tab fetch" but that causes cache miss on first render
      scheduleIdle(() => {
        perfLog('home_prefetch_start', { userId: userId.slice(0, 8) });
        prefetchTabDataInternal(queryClient, "home", userId).then(() => {
          perfLog('home_prefetch_done');
        });
      }, "high");
      
      // Priority 2: Appointments (highest priority per requirements)
      scheduleIdle(() => {
        prefetchTabDataInternal(queryClient, "appointments", userId);
      }, "high");

      // Priority 3: Meds (daily use)
      scheduleIdle(() => {
        perfLog('meds_prefetch_start', { userId: userId.slice(0, 8) });
        prefetchTabDataInternal(queryClient, "meds", userId).then(() => {
          perfLog('meds_prefetch_done');
        });
      }, "high");

      // Priority 4: Track (frequent use)
      scheduleIdle(() => {
        perfLog('track_prefetch_start', { userId: userId.slice(0, 8) });
        prefetchTabDataInternal(queryClient, "track", userId).then(() => {
          perfLog('track_prefetch_done');
        });
      }, "low");

      // Priority 5: More + Beta (less frequent)
      scheduleIdle(() => {
        perfLog('more_prefetch_start', { userId: userId.slice(0, 8) });
        prefetchTabDataInternal(queryClient, "more", userId).then(() => {
          perfLog('more_prefetch_done');
        });
        prefetchTabDataInternal(queryClient, "beta", userId);
      }, "low");

      // Priority 6: Standalone pages (Care Team, History) - HIGHER PRIORITY
      // Move to "high" priority since these are the slowest pages
      scheduleIdle(() => {
        prefetchPageDataInternal(queryClient, "care-team", userId);
        prefetchPageDataInternal(queryClient, "history", userId);
      }, "high");
    });

  }, [isAuthReady, user?.id, queryClient]);
}

/**
 * Resets prefetch tracking (useful on logout or session change)
 */
export function resetPrefetchedTabs() {
  prefetchedTabs.clear();
}
